import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Navbar from '../../components/Navbar';
import Modal from '../../components/Modal';
import api from '../../utils/api';

const AdminDashboard = () => {
  const { showToast } = useToast();
  const [organizers, setOrganizers] = useState([]);
  const [stats, setStats] = useState({ totalOrganizers: 0, activeOrganizers: 0, inactiveOrganizers: 0 });
  const [loading, setLoading] = useState(true);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, orgId: null });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrganizers = organizers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(organizers.length / itemsPerPage);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, orgsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/organizers')
      ]);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (orgsRes.data.success) setOrganizers(orgsRes.data.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleRegeneratePasswordClick = (id) => {
    setConfirmModal({ isOpen: true, orgId: id });
  };

  const confirmRegeneratePassword = async () => {
    const id = confirmModal.orgId;
    setConfirmModal({ isOpen: false, orgId: null });
    try {
      const res = await api.put(`/admin/organizers/${id}/regenerate`);
      if (res.data.success) {
        showToast(res.data.message || 'Password regenerated successfully', 'success');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error regenerating password', 'error');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await api.put(`/admin/organizers/${id}/toggle-status`);
      if (res.data.success) {
        fetchData();
        showToast(res.data.message || 'Status updated', 'success');
      }
    } catch (error) {
      showToast('Error toggling status', 'error');
    }
  };


  // Pagination Handlers
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return (
    <>
      <Navbar />
      <div className="p-8 text-center text-gray-500">Loading Dashboard...</div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-gray-600">Manage conference organizers and platform credentials.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <div className="p-2">
            <h3 className="text-blue-100 text-sm font-medium">Total Organizers</h3>
            <p className="text-3xl font-bold mt-1">{stats.totalOrganizers}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
          <div className="p-2">
            <h3 className="text-emerald-100 text-sm font-medium">Active Organizers</h3>
            <p className="text-3xl font-bold mt-1">{stats.activeOrganizers}</p>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-rose-500 to-red-600 text-white border-0">
          <div className="p-2">
            <h3 className="text-rose-100 text-sm font-medium">Inactive Organizers</h3>
            <p className="text-3xl font-bold mt-1">{stats.inactiveOrganizers}</p>
          </div>
        </Card>
      </div>

      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Organizer Management</h2>
      </div>

      {/* Organizers List */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organizer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login / Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No organizers found.</td>
                </tr>
              ) : (
                currentOrganizers.map((org) => (
                  <tr key={org._id} className={!org.isActive ? 'bg-gray-50 opacity-75' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">{org.organization || 'No org specified'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{org.email}</div>
                      <div className="text-sm text-gray-500">{org.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                        {org.username || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        org.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {org.isActive ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => handleRegeneratePasswordClick(org._id)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                        Regenerate Password
                      </button>
                      <button onClick={() => handleToggleStatus(org._id)} className={`${org.isActive ? 'text-red-600 hover:text-red-900' : 'text-emerald-600 hover:text-emerald-900'}`}>
                        {org.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} variant="outline">
                Previous
              </Button>
              <Button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} variant="outline">
                Next
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, organizers.length)}</span> of <span className="font-medium">{organizers.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => paginate(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Modal 
        isOpen={confirmModal.isOpen} 
        onClose={() => setConfirmModal({ isOpen: false, orgId: null })}
        title="Regenerate Password"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to regenerate the password for this organizer? The new password will be emailed to them automatically.
        </p>
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={() => setConfirmModal({ isOpen: false, orgId: null })}>Cancel</Button>
          <Button variant="primary" onClick={confirmRegeneratePassword}>Regenerate Password</Button>
        </div>
      </Modal>

    </div>
    </>
  );
};

export default AdminDashboard;
